import { useCallback, useEffect, useRef, useState } from "react";
import { useActiveContext } from "../contexts/ActiveContextContext";
import postsService from "../services/postsService";

export interface ContentEntry {
  id: number;
  name: string;
  type: "url" | "text" | "prompt" | "file";
  original_data: string;
  processed_content: any;
  platforms: string[];
  business_type: string;
  created_at: string;
  status: string;
}

/**
 * @param options.autoLoad carrega sozinho ao montar/trocar de contexto (padrão true). Telas
 *   paginadas que controlam a própria busca (ContentList) passam false: duas buscas simultâneas
 *   (limite 50 do hook × página de 8 da tela) faziam a contagem alternar entre 8 e 20.
 */
export function useContentLibrary(
  options: { autoLoad?: boolean; loadAll?: boolean } = {},
) {
  // loadAll: telas que agregam/listam a biblioteca inteira (análise, sugestões de agenda) —
  // sem ele, estatísticas e listas ficavam limitadas aos primeiros 50 itens.
  const { autoLoad = true, loadAll = false } = options;
  const { activeContext } = useActiveContext();
  const [contentLibrary, setContentLibrary] = useState<ContentEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  // Contagem por tipo da biblioteca inteira do contexto (vem do backend, não da página carregada)
  const [countsByType, setCountsByType] = useState<{
    url: number;
    text: number;
    prompt: number;
    file: number;
  } | null>(null);
  const [page, setPage] = useState(1);

  const contextId = `${activeContext.type}-${activeContext.organization?.id || ""}-${activeContext.clientCompany?.id || ""}`;
  const contextIdRef = useRef(contextId);

  // Sync ref with current contextId
  contextIdRef.current = contextId;

  // Última página/tamanho pedidos: recargas internas (ex.: após excluir) reusam os mesmos.
  const lastRequestRef = useRef({ page: 1, limit: 50 });

  const loadContentLibrary = useCallback(
    async (targetPage: number = 1, targetLimit: number = 50) => {
      const requestContextId = contextIdRef.current;
      lastRequestRef.current = { page: targetPage, limit: targetLimit };
      setLoading(true);
      setError(null);
      try {
        const response = await postsService.getContentLibrary(
          targetPage,
          targetLimit,
        );

        // CHECK: If context changed while fetching, ignore results
        if (contextIdRef.current !== requestContextId) {
          return;
        }

        const contents = response?.contents || [];
        const totalCount = response?.total || 0;

        // Mapear dados do backend para o formato esperado pelo frontend
        const mappedContents: ContentEntry[] = contents.map((item: any) => {
          const rawData = item.original_data || item.content || "";
          const backendType = (
            item.type ||
            item.content_type ||
            "text"
          ).toLowerCase();
          let detectedType: "url" | "text" | "prompt" | "file" = "text";

          // Mapeia tipos conhecidos do backend para os do frontend
          if (backendType === "url" || backendType === "link") {
            detectedType = "url";
          } else if (
            backendType === "prompt" ||
            backendType === "ai_prompt" ||
            backendType === "template"
          ) {
            detectedType = "prompt";
          } else if (backendType === "file" || backendType === "document") {
            detectedType = "file";
          } else if (backendType === "snippet") {
            detectedType = "text";
          } else if (backendType === "article") {
            // Artigos no backend podem ser URLs extraídas ou arquivos processados
            if (
              rawData.trim().startsWith("http://") ||
              rawData.trim().startsWith("https://")
            ) {
              detectedType = "url";
            } else if (
              rawData.includes("Arquivo PDF:") ||
              rawData.includes("Arquivo:") ||
              rawData.includes("Página 1")
            ) {
              detectedType = "file";
            } else {
              detectedType = "text";
            }
          } else {
            detectedType = "text"; // Fallback
          }

          // Heurística de segurança final: se o conteúdo é obviamente uma URL e não foi detectado
          if (
            detectedType === "text" &&
            (rawData.trim().startsWith("http://") ||
              rawData.trim().startsWith("https://"))
          ) {
            detectedType = "url";
          }

          return {
            id: item.id,
            name: item.name || item.title || `Conteúdo ${item.id}`,
            type: detectedType,
            original_data: rawData,
            processed_content: item.processed_content || null,
            platforms: item.platforms || ["linkedin", "facebook"],
            business_type: item.business_type || "b2b",
            created_at: item.created_at || new Date().toISOString(),
            status: item.status || "active",
          };
        });

        // Remover duplicatas baseado no ID (manter o mais recente se houver duplicatas)
        const uniqueContents = Array.from(
          new Map(mappedContents.map((item) => [item.id, item])).values(),
        );

        setContentLibrary(uniqueContents);
        setTotal(totalCount);
        setCountsByType(response?.counts_by_type ?? null);
        setPage(targetPage);
      } catch (err: any) {
        setError(err.message || "Erro ao carregar conteúdos");
        setContentLibrary([]);
        setTotal(0);
        setCountsByType(null);
      } finally {
        setLoading(false);
      }
    },
    [
      activeContext.type,
      activeContext.organization?.id,
      activeContext.clientCompany?.id,
    ],
  );

  useEffect(() => {
    // Quando o contexto muda, resetamos a lista IMEDIATAMENTE para mostrar o skeleton
    // e evitar o "atraso visual" de ver dados da empresa anterior.
    setContentLibrary([]);
    if (!autoLoad) return;
    setLoading(true);
    loadContentLibrary();
  }, [loadContentLibrary, autoLoad]);

  // Se a primeira página não trouxe tudo, recarrega uma vez com o total informado pelo backend.
  useEffect(() => {
    if (!loadAll || loading) return;
    if (total > contentLibrary.length && lastRequestRef.current.limit < total) {
      loadContentLibrary(1, total);
    }
  }, [loadAll, loading, total, contentLibrary.length, loadContentLibrary]);

  const deleteContent = useCallback(
    async (contentId: number) => {
      setLoading(true);
      setError(null);
      try {
        const result = await postsService.deleteContent(contentId);
        setContentLibrary((prev) =>
          prev.filter((item) => item.id !== contentId),
        );
        const { page: lastPage, limit: lastLimit } = lastRequestRef.current;
        await loadContentLibrary(lastPage, lastLimit);
        return result;
      } catch (err: any) {
        setError(err.message || "Erro ao excluir conteúdo");
        throw err; // Re-throw para que o componente possa tratar
      } finally {
        setLoading(false);
      }
    },
    [loadContentLibrary],
  );

  return {
    contentLibrary,
    loading,
    error,
    total,
    countsByType,
    page,
    loadContentLibrary,
    deleteContent,
  };
}
